import AccessTime from '@mui/icons-material/AccessTime'
import Login from '@mui/icons-material/Login'
import Visibility from '@mui/icons-material/Visibility'
import Videocam from '@mui/icons-material/Videocam'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import LocalHospital from '@mui/icons-material/LocalHospital'
import People from '@mui/icons-material/People'
import Refresh from '@mui/icons-material/Refresh'
import { alpha } from '@mui/material/styles'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import {
  checkIntoRoom,
  peekIntoRoom,
  refreshCheckIntoRoom,
} from 'src/clinic/components/CheckIn/services'
import {
  broadcastCheckInUpdate,
  updateCheckInWithValidation,
} from 'src/clinic/components/CheckIn/checkInSyncService'
import {
  getEventTypesFromCheckInOption,
  getEventTypesFromRoomData,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import { allAvailableEventTypes } from 'src/consumer/constants'
import ChamberRoomStatusIndicator from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberRoomStatusIndicator'
import CheckinRequestPromptDialog from 'src/mentor/components/CheckinRequestPromptDialog'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import {
  createEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'
import { EMPTY_ROSTER_ASSIGNMENT_INDEX } from '../rosterAssignments'
import WaitingPatientsStrip from './WaitingPatientsStrip'
import { HighlightedText } from './clinicSearch'

const IN_PERSON_CHECKIN_METHOD = 'check-in-method-in-person'
const REMOTE_CHECKIN_METHOD = 'check-in-method-remote'

const ROOM_SERVICE_CONFIG = [
  {
    key: 'consultation',
    label: 'Consultation',
    roleFlag: 'isDoctor',
    eventTypes: [allAvailableEventTypes.consultation],
  },
  {
    key: 'therapy',
    label: 'Therapy',
    roleFlag: 'isTherapist',
    eventTypes: [allAvailableEventTypes.therapySession, allAvailableEventTypes.assessment],
  },
  {
    key: 'screening',
    label: 'Screening',
    roleFlag: 'isScreener',
    eventTypes: [allAvailableEventTypes.screening],
  },
]

const ROOM_SERVICE_CONFIG_MAP = ROOM_SERVICE_CONFIG.reduce((accumulator, serviceConfig) => {
  accumulator[serviceConfig.key] = serviceConfig
  return accumulator
}, {})

function getPractitionerServicesFromAuthorities() {
  const allRoles = getAllRolesInBoolean()
  return ROOM_SERVICE_CONFIG.filter((serviceConfig) => allRoles?.[serviceConfig.roleFlag])
}

function getEventTypeIdentity(eventType) {
  return (
    eventType?.uid ||
    eventType?.id ||
    `${eventType?.type || 'unknown'}-${eventType?.contextId || 'global'}-${
      eventType?.title || eventType?.name || 'event'
    }`
  )
}

function dedupeEventTypes(eventTypes = []) {
  const seenEventTypeIds = new Set()

  return eventTypes.filter((eventType) => {
    if (!eventType) return false

    const identity = getEventTypeIdentity(eventType)
    if (seenEventTypeIds.has(identity)) return false

    seenEventTypeIds.add(identity)
    return true
  })
}

function getEnabledClinicEventTypes(mentorEventTypes = [], clinicId) {
  return mentorEventTypes.filter(
    (eventType) =>
      eventType?.enabled !== false && (!clinicId || !eventType?.contextId || eventType?.contextId === clinicId),
  )
}

function getRoomServiceKeysFromEventTypes(eventTypes = []) {
  const roomServiceKeys = new Set()

  eventTypes.forEach((eventType) => {
    const eventTypeValue = typeof eventType === 'string' ? eventType : eventType?.type

    ROOM_SERVICE_CONFIG.forEach((serviceConfig) => {
      if (serviceConfig.eventTypes.includes(eventTypeValue)) {
        roomServiceKeys.add(serviceConfig.key)
      }
    })
  })

  return [...roomServiceKeys]
}

function filterEventTypesForService(eventTypes = [], serviceKey) {
  const allowedEventTypes = ROOM_SERVICE_CONFIG_MAP?.[serviceKey]?.eventTypes || []

  return dedupeEventTypes(
    eventTypes.filter((eventType) => allowedEventTypes.includes(eventType?.type)),
  )
}

function getCheckInOptionSearchText(checkInOption) {
  return [checkInOption?.title, checkInOption?.name, checkInOption?.id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

// Phase 14bi — id-stable map of check-in option ids to the service keys they imply.
// Mirrors the backend's CHECKIN_EVENT_OPTION_* constants in ChamberRoom.java. Pulled
// out of getRoomServiceKeysFromOptionText so the title-based heuristic doesn't fight
// the id-based truth: now that the backend rewrites the therapy title to "Psychotherapy
// only" / "Nutrition Consultation only" per practitioner, a substring match for
// "consult" would mis-classify those as the consultation service. The id is stable
// across discipline rewrites and across future renames of the title text.
const CHECKIN_OPTION_ID_TO_SERVICE_KEYS = {
  'consultation-only': ['consultation'],
  'therapy-only': ['therapy'],
  'screening-only': ['screening'],
  'therapy-and-or-screening': ['therapy', 'screening'],
}

function getRoomServiceKeysFromOptionText(checkInOption) {
  // Phase 14bi — prefer the stable id mapping when the option carries a known id.
  // Fall back to title-text heuristics only for legacy / unknown ids so out-of-tree
  // callers seeing this format keep working.
  if (checkInOption?.id && CHECKIN_OPTION_ID_TO_SERVICE_KEYS[checkInOption.id]) {
    return [...CHECKIN_OPTION_ID_TO_SERVICE_KEYS[checkInOption.id]]
  }
  const optionSearchText = getCheckInOptionSearchText(checkInOption)
  const roomServiceKeys = new Set()

  // Order matters: check therapy/screening BEFORE consult, because the dynamic
  // discipline rewrite turns therapy titles into things like "Nutrition Consultation
  // only" which would otherwise match the 'consult' branch first.
  if (optionSearchText.includes('therapy') || optionSearchText.includes('assessment')) {
    roomServiceKeys.add('therapy')
  }
  if (optionSearchText.includes('screen')) {
    roomServiceKeys.add('screening')
  }
  if (
    optionSearchText.includes('consult') &&
    !optionSearchText.includes('nutrition consult') &&
    !optionSearchText.includes('nutrition-consult')
  ) {
    // "Consultation only" → consultation. "Nutrition Consultation only" → therapy
    // (caught above). The exclusion guards against mid-substring overlap when a
    // legacy / unknown id slips through.
    roomServiceKeys.add('consultation')
  }

  return [...roomServiceKeys]
}

function getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId) {
  const matchedEventTypes = getEventTypesFromCheckInOption(checkInOption, mentorEventTypes, clinicId)
  const directEventTypes = Array.isArray(checkInOption?.eventTypes)
    ? checkInOption.eventTypes.filter(
        (eventType) =>
          eventType?.enabled !== false &&
          (!clinicId || !eventType?.contextId || eventType?.contextId === clinicId),
      )
    : []

  return dedupeEventTypes([...matchedEventTypes, ...directEventTypes])
}

function getRoomEventTypes(roomData, clinicId, mentorEventTypes = []) {
  const helperEventTypes = getEventTypesFromRoomData(
    roomData?.checkInEventOptions,
    mentorEventTypes,
    clinicId,
  )
  const optionEventTypes = (roomData?.checkInEventOptions || []).flatMap((checkInOption) =>
    getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
  )

  return dedupeEventTypes([...helperEventTypes, ...optionEventTypes])
}

function getRoomServiceKeysFromCheckInOption(checkInOption, mentorEventTypes, clinicId) {
  return [
    ...new Set([
      ...getRoomServiceKeysFromEventTypes(
        getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
      ),
      ...getRoomServiceKeysFromOptionText(checkInOption),
    ]),
  ]
}

function resolveCheckInOptionForService(roomData, serviceKey, mentorEventTypes = [], clinicId) {
  const roomOptions = roomData?.checkInEventOptions || []

  return roomOptions
    .map((checkInOption, index) => {
      const roomServiceKeys = getRoomServiceKeysFromCheckInOption(
        checkInOption,
        mentorEventTypes,
        clinicId,
      )

      if (!roomServiceKeys.includes(serviceKey)) {
        return null
      }

      const matchedEventTypes = filterEventTypesForService(
        getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
        serviceKey,
      )
      const optionSearchText = getCheckInOptionSearchText(checkInOption)
      const hasSingleService = roomServiceKeys.length === 1
      const hasPreferredKeyword =
        serviceKey === 'consultation'
          ? optionSearchText.includes('consult')
          : serviceKey === 'therapy'
          ? optionSearchText.includes('therapy') || optionSearchText.includes('assessment')
          : optionSearchText.includes('screen')

      return {
        checkInOption,
        matchedEventTypes,
        score:
          (hasSingleService ? 0 : 10) +
          (matchedEventTypes.length > 0 ? 0 : 3) +
          (hasPreferredKeyword ? 0 : 1) +
          index / 100,
      }
    })
    .filter(Boolean)
    .sort((optionA, optionB) => optionA.score - optionB.score)[0]
}

// Phase 14bl — list-view button row is now driven by `roomData.checkInEventOptions`
// directly, NOT by the practitioner's JWT authorities. Reason: the backend's
// PractitionerChamberRoomService.getCheckInEventOptionsForRoom decides which options
// to expose by checking the clinic's contact lists
// (isMentorTherapistInClinicLocation / isMentorScreenerInClinicLocation /
// isMentorDoctorInClinicLocation) — NOT the practitioner's JWT roles. A practitioner
// can be a registered screener in a clinic's `screeners` list while their KYC
// servicesApproved (and therefore their JWT) only carries ROLE_THERAPIST. The popup
// (CheckIn dialog) has always rendered roomData.checkInEventOptions verbatim and so
// surfaces every option the backend approved. The list view used to first filter
// through practitioner-services-from-JWT and then resolve a check-in option per
// service — which dropped any option whose service the JWT didn't carry. The fix is
// straightforward: iterate the server-blessed options and render one entry per
// option, with discipline-aware label cleanup applied where applicable.
const CHECKIN_OPTION_LABEL_CLEANER = {
  // For single-service options, drop the trailing " only" so a standalone button
  // reads cleanly ("Psychotherapy" not "Psychotherapy only"). The popup keeps the
  // " only" since it acts as a qualifier between siblings.
  'consultation-only': (title) => String(title || '').replace(/\s+only$/i, '').trim(),
  'therapy-only': (title) => String(title || '').replace(/\s+only$/i, '').trim(),
  'screening-only': (title) => String(title || '').replace(/\s+only$/i, '').trim(),
  // Combined option keeps the "and/or Screening" tail intact — that's the phrase,
  // not a removable qualifier.
  'therapy-and-or-screening': (title) => String(title || '').trim(),
}

function cleanCheckInOptionLabel(checkInOption, fallbackLabel) {
  const cleaner = CHECKIN_OPTION_LABEL_CLEANER[checkInOption?.id]
  const cleaned = cleaner
    ? cleaner(checkInOption?.title)
    : String(checkInOption?.title || '').trim()
  return cleaned || fallbackLabel || 'Check in'
}

function getRoomServiceEntries(roomData, clinicId, mentorEventTypes = []) {
  const clinicScopedMentorEventTypes = getEnabledClinicEventTypes(mentorEventTypes, clinicId)
  const roomEventTypes = getRoomEventTypes(roomData, clinicId, clinicScopedMentorEventTypes)
  const checkInOptions = (roomData?.checkInEventOptions || []).filter(
    (option) => option && option.id,
  )

  if (checkInOptions.length === 0) {
    return []
  }

  return checkInOptions
    .map((checkInOption) => {
      const optionId = checkInOption.id
      // Resolve which ROOM_SERVICE_CONFIG entries this option implies (one for
      // singles, two for the combined option). Used downstream for filtering event
      // types + for the deprecated `serviceEntry.roleFlag` consumers (mostly the
      // Supports X label-formatting; nothing on the critical path now).
      const serviceKeys = CHECKIN_OPTION_ID_TO_SERVICE_KEYS[optionId] ||
        getRoomServiceKeysFromOptionText(checkInOption)
      // Pull the matching event types from BOTH the practitioner's enabled event
      // list (preferred — these are the events that will actually be bookable for
      // this practitioner at this clinic) and the room's own event types as a
      // fallback when the practitioner has nothing on file yet (a fresh hire that
      // hasn't created event-types yet still gets a usable check-in flow).
      const matchedFromPractitioner = serviceKeys.flatMap((serviceKey) =>
        filterEventTypesForService(clinicScopedMentorEventTypes, serviceKey),
      )
      const matchedFromRoom = serviceKeys.flatMap((serviceKey) =>
        filterEventTypesForService(roomEventTypes, serviceKey),
      )
      const dedupedRoomEventTypes = dedupeEventTypes(
        matchedFromPractitioner.length > 0 ? matchedFromPractitioner : matchedFromRoom,
      )

      // Resolve the legacy ROOM_SERVICE_CONFIG entry to provide a fallback label
      // and roleFlag for any downstream consumer that hasn't migrated to reading
      // checkInOption.title. For the combined option, pick the therapy config so
      // role-flag gating isn't accidentally screener-only.
      const primaryServiceKey = serviceKeys[0] || 'therapy'
      const primaryServiceConfig = ROOM_SERVICE_CONFIG_MAP[primaryServiceKey] || ROOM_SERVICE_CONFIG[1]

      return {
        // `key` must be the option id so handleDirectCheckIn distinguishes the
        // combined entry from its single-service siblings (each click maps to a
        // distinct selectedCheckinEventOption posted to the backend).
        key: optionId,
        label: cleanCheckInOptionLabel(checkInOption, primaryServiceConfig?.label),
        roleFlag: primaryServiceConfig?.roleFlag,
        eventTypes: primaryServiceConfig?.eventTypes || [],
        serviceKeys,
        checkInOption,
        roomEventTypes: dedupedRoomEventTypes,
      }
    })
    .filter(Boolean)
}

function getAuditPractitionerName(roomData, isCurrentPractitionerRoom = false) {
  const practitionerName = roomData?.checkedInMentorName?.trim()

  if (practitionerName) {
    return practitionerName
  }

  return isCurrentPractitionerRoom ? 'Your Session' : 'Other Practitioner'
}

function getStoredCheckInOptionId(checkedInDetails = null) {
  return (
    checkedInDetails?.selectedCheckInEventOption ||
    checkedInDetails?.selectedCheckinEventOption ||
    null
  )
}

function getCheckInMethodOptions(roomData) {
  const checkInMethodOptions = Array.isArray(roomData?.checkInMethodOptions)
    ? roomData.checkInMethodOptions
    : []

  const normalizedMethodOptions =
    checkInMethodOptions.length > 0
      ? checkInMethodOptions
      : [
          {
            id: IN_PERSON_CHECKIN_METHOD,
            title: 'In Person',
            description: 'See patients physically at the clinic.',
            enabled: true,
          },
        ]

  const methodPriorityMap = {
    [IN_PERSON_CHECKIN_METHOD]: 0,
    [REMOTE_CHECKIN_METHOD]: 1,
  }

  return [...normalizedMethodOptions].sort((methodOptionA, methodOptionB) => {
    const priorityA = methodPriorityMap?.[methodOptionA?.id] ?? 99
    const priorityB = methodPriorityMap?.[methodOptionB?.id] ?? 99

    if (priorityA !== priorityB) return priorityA - priorityB
    return (methodOptionA?.title || '').localeCompare(methodOptionB?.title || '')
  })
}

function isCheckInMethodEnabled(methodOption) {
  return methodOption?.enabled !== false
}

function getDefaultCheckInMethod(
  methodOptions = [],
  preferredMethodId = null,
  preferRemoteByDefault = false,
) {
  const enabledMethodOptions = methodOptions.filter(isCheckInMethodEnabled)
  const candidateMethodOptions = enabledMethodOptions.length > 0 ? enabledMethodOptions : methodOptions

  if (preferredMethodId && candidateMethodOptions.some((methodOption) => methodOption?.id === preferredMethodId)) {
    return preferredMethodId
  }

  if (
    preferRemoteByDefault &&
    candidateMethodOptions.some((methodOption) => methodOption?.id === REMOTE_CHECKIN_METHOD)
  ) {
    return REMOTE_CHECKIN_METHOD
  }

  if (candidateMethodOptions.some((methodOption) => methodOption?.id === IN_PERSON_CHECKIN_METHOD)) {
    return IN_PERSON_CHECKIN_METHOD
  }

  return candidateMethodOptions?.[0]?.id || null
}


function getRoomStatusFromScreenStatuses(chamberRoomId, screenStatuses) {
  if (!chamberRoomId || !Array.isArray(screenStatuses) || screenStatuses.length === 0) return 'unknown'
  const roomScreens = screenStatuses.filter((item) => item?.chamberRoomId === chamberRoomId)
  if (roomScreens.length === 0) return 'unknown'
  return roomScreens.some((item) => item?.status === 'up') ? 'online' : 'offline'
}

function getClinicLocationStatus(location) {
  const scheduleTimezone = location?.schedules?.[0]?.timezone
    ? decodeURIComponent(location?.schedules?.[0]?.timezone)
    : '+05:30'
  const weeklyHourMap = location?.schedules?.[0]?.weeklyHourMap

  if (!weeklyHourMap) {
    return {
      status: 'unknown',
      label: 'Schedule unavailable',
      accent: '#64748b',
    }
  }

  const currentDay = dayjs().format('dddd').toLowerCase()
  const todayTimings = weeklyHourMap?.[currentDay]

  if (!todayTimings?.available || !todayTimings?.slotList?.length) {
    return {
      status: 'closed',
      label: 'Closed today',
      accent: '#94a3b8',
    }
  }

  const now = dayjs().utcOffset(scheduleTimezone)
  let nextOpening = null

  for (const slot of todayTimings.slotList) {
    const [startHours, startMinutes] = slot?.startTime?.split(':') || [0, 0]
    const [endHours, endMinutes] = slot?.endTime?.split(':') || [0, 0]
    const todayStart = now.startOf('day')
    const slotStart = todayStart.hour(parseInt(startHours)).minute(parseInt(startMinutes)).second(0)
    const slotEnd = todayStart.hour(parseInt(endHours)).minute(parseInt(endMinutes)).second(0)

    if (now.isAfter(slotStart) && now.isBefore(slotEnd)) {
      const minutesUntilClose = slotEnd.diff(now, 'minute')
      if (minutesUntilClose <= 30) {
        return {
          status: 'closing-soon',
          label: `Closes in ${minutesUntilClose} min`,
          accent: '#d97706',
        }
      }

      return {
        status: 'open',
        label: `Open until ${slotEnd.format('h:mm A')}`,
        accent: '#059669',
      }
    }

    if (!nextOpening && slotStart.isAfter(now)) {
      nextOpening = slotStart
    }
  }

  if (nextOpening) {
    const minutesUntilOpen = nextOpening.diff(now, 'minute')
    if (minutesUntilOpen <= 60) {
      return {
        status: 'opening-soon',
        label: `Opens in ${minutesUntilOpen} min`,
        accent: '#2563eb',
      }
    }

    return {
      status: 'closed',
      label: `Opens at ${nextOpening.format('h:mm A')}`,
      accent: '#64748b',
    }
  }

  return {
    status: 'closed',
    label: 'Closed for today',
    accent: '#94a3b8',
  }
}

function BrandIcon({ brand }) {
  const brandImage = getImageLink(brand?.coverIcon || brand?.icon, brand?.coverImage, false)

  if (brandImage) {
    return (
      <Box
        component="img"
        src={brandImage}
        alt={brand?.title}
        sx={{
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          borderRadius: '18px',
          objectFit: 'cover',
          border: '1px solid rgba(124, 58, 237, 0.12)',
          boxShadow: '0 12px 24px rgba(124, 58, 237, 0.12)',
          backgroundColor: 'white',
          flexShrink: 0,
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        width: { xs: 48, sm: 56 },
        height: { xs: 48, sm: 56 },
        borderRadius: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.16) 0%, rgba(14, 165, 233, 0.12) 100%)',
        border: '1px solid rgba(124, 58, 237, 0.12)',
        boxShadow: '0 12px 24px rgba(124, 58, 237, 0.08)',
        flexShrink: 0,
      }}
    >
      <LocalHospital sx={{ color: 'rgba(124, 58, 237, 0.9)', fontSize: { xs: 24, sm: 28 } }} />
    </Box>
  )
}

function RoomOperationsRow({
  roomData,
  clinicLocationData,
  checkedInDetails,
  onRefreshChamberStatuses,
  screenStatusesList,
  rosterAssignmentGroup = null,
  searchQuery = '',
}) {
  const navigate = useNavigate()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [refreshing, setRefreshing] = useState(false)
  const [activeCheckInService, setActiveCheckInService] = useState(null)
  const [requestCheckoutDialogOpen, setRequestCheckoutDialogOpen] = useState(false)
  const [requestCheckoutLoading, setRequestCheckoutLoading] = useState(false)
  const checkInMethodOptions = useMemo(() => getCheckInMethodOptions(roomData), [roomData])
  const practitionerServices = useMemo(() => getPractitionerServicesFromAuthorities(), [])
  const preferRemoteByDefault = useMemo(
    () =>
      practitionerServices.some(
        (serviceConfig) =>
          serviceConfig?.key === 'therapy' || serviceConfig?.key === 'screening',
      ),
    [practitionerServices],
  )
  // Phase 14bl — getRoomServiceEntries no longer takes practitionerServices; the
  // server-side roomData.checkInEventOptions is the source of truth for which
  // options apply to this practitioner+room (the backend already verified clinic
  // membership before exposing each option). practitionerServices is still used
  // for unrelated UI bits like preferRemoteByDefault below.
  const roomServiceEntries = useMemo(
    () =>
      getRoomServiceEntries(
        roomData,
        clinicLocationData?.id,
        mentorProfileData?.eventTypes || [],
      ),
    [clinicLocationData?.id, mentorProfileData?.eventTypes, roomData],
  )

  const roomOccupied = roomData?.occupied
  const occupiedByThisMentor =
    roomData?.occupied && roomData?.checkedInMentorId === mentorProfileData?.id
  const isCurrentPractitionerRoom =
    occupiedByThisMentor ||
    (checkedInDetails?.chamberRoomId === roomData?.id &&
      checkedInDetails?.clinicLocationId === clinicLocationData?.id)
  const isRosterAssignedRoom = Boolean(rosterAssignmentGroup?.assignmentCount)
  const [selectedCheckInMethod, setSelectedCheckInMethod] = useState(() =>
    getDefaultCheckInMethod(checkInMethodOptions, null, preferRemoteByDefault),
  )
  const isCheckedInRoom =
    checkedInDetails?.chamberRoomId === roomData?.id &&
    checkedInDetails?.clinicLocationId === clinicLocationData?.id
  const roomScreenStatus = getRoomStatusFromScreenStatuses(roomData?.id, screenStatusesList)
  // Only rooms with an AUMHUM screen explicitly installed have a status to monitor — show the
  // online/offline indicator + refresh for those only (consistent with the card view and the
  // support portal's `=== true` check). Anything else (no screen / unset) shows nothing.
  const hasScreen = roomData?.hasAUMHUMScreenInstalled === true
  const storedCheckInOptionId = getStoredCheckInOptionId(checkedInDetails)
  const preferredRoomServiceEntry = useMemo(() => {
    if (!roomServiceEntries.length) return null

    const matchedStoredEntry = storedCheckInOptionId
      ? roomServiceEntries.find(
          (serviceEntry) => serviceEntry?.checkInOption?.id === storedCheckInOptionId,
        )
      : null

    if (matchedStoredEntry) {
      return matchedStoredEntry
    }

    if (practitionerServices.length === 1) {
      // Phase 14bl — entry.key is now the option id (e.g. 'therapy-only', not the
      // service key 'therapy'), so we match through the entry's `serviceKeys` array
      // populated from CHECKIN_OPTION_ID_TO_SERVICE_KEYS. Prefers a single-service
      // entry over the combined `therapy-and-or-screening` so a sole therapist
      // defaults to the focused therapy button rather than the multi-service one.
      const practitionerServiceKey = practitionerServices?.[0]?.key
      const singleServiceMatch = roomServiceEntries.find(
        (serviceEntry) =>
          (serviceEntry?.serviceKeys || []).length === 1 &&
          (serviceEntry?.serviceKeys || []).includes(practitionerServiceKey),
      )
      if (singleServiceMatch) {
        return singleServiceMatch
      }
      const anyMatch = roomServiceEntries.find((serviceEntry) =>
        (serviceEntry?.serviceKeys || []).includes(practitionerServiceKey),
      )
      if (anyMatch) {
        return anyMatch
      }
    }

    return roomServiceEntries.find((serviceEntry) => Boolean(serviceEntry?.checkInOption?.id)) || null
  }, [practitionerServices, roomServiceEntries, storedCheckInOptionId])
  const inferredCurrentRoomMethodId = useMemo(() => {
    if (!isCurrentPractitionerRoom) {
      return selectedCheckInMethod
    }

    const persistedMethodId = checkedInDetails?.checkinMethod || checkedInDetails?.checkInMethod
    if (persistedMethodId) {
      return persistedMethodId
    }

    const hasEnabledRemoteMethod = checkInMethodOptions.some(
      (methodOption) =>
        methodOption?.id === REMOTE_CHECKIN_METHOD && isCheckInMethodEnabled(methodOption),
    )

    if (hasEnabledRemoteMethod && roomData?.activeAuditChannelId) {
      return REMOTE_CHECKIN_METHOD
    }

    return getDefaultCheckInMethod(checkInMethodOptions, IN_PERSON_CHECKIN_METHOD, false)
  }, [
    checkInMethodOptions,
    checkedInDetails?.checkInMethod,
    checkedInDetails?.checkinMethod,
    isCurrentPractitionerRoom,
    roomData?.activeAuditChannelId,
    selectedCheckInMethod,
  ])
  const activeRoomMethodId = isCurrentPractitionerRoom
    ? inferredCurrentRoomMethodId
    : selectedCheckInMethod
  const canAuditAnotherPractitioner = Boolean(
    !isCurrentPractitionerRoom && roomData?.canVideoAudit && roomData?.activeAuditChannelId,
  )

  useEffect(() => {
    const preferredMethodId = isCurrentPractitionerRoom
      ? checkedInDetails?.checkinMethod || checkedInDetails?.checkInMethod
      : null

    setSelectedCheckInMethod((previousSelectedCheckInMethod) => {
      const validCurrentSelection = checkInMethodOptions.some(
        (methodOption) =>
          methodOption?.id === previousSelectedCheckInMethod && isCheckInMethodEnabled(methodOption),
      )

      if (preferredMethodId && preferredMethodId !== previousSelectedCheckInMethod) {
        return getDefaultCheckInMethod(
          checkInMethodOptions,
          preferredMethodId,
          preferRemoteByDefault,
        )
      }

      if (validCurrentSelection) {
        return previousSelectedCheckInMethod
      }

      return getDefaultCheckInMethod(
        checkInMethodOptions,
        preferredMethodId,
        preferRemoteByDefault,
      )
    })
  }, [
    checkInMethodOptions,
    checkedInDetails?.checkInMethod,
    checkedInDetails?.checkinMethod,
    isCurrentPractitionerRoom,
    preferRemoteByDefault,
  ])

  const tone = isCurrentPractitionerRoom
    ? {
        accent: '#7c3aed',
        background:
          'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(255, 255, 255, 0.96) 100%)',
        border: 'rgba(124, 58, 237, 0.26)',
      }
    : isRosterAssignedRoom
    ? {
        accent: '#0284c7',
        background:
          'linear-gradient(135deg, rgba(2, 132, 199, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
        border: 'rgba(2, 132, 199, 0.2)',
      }
    : roomOccupied
    ? {
        accent: '#e11d48',
        background:
          'linear-gradient(135deg, rgba(225, 29, 72, 0.08) 0%, rgba(255, 255, 255, 0.96) 100%)',
        border: 'rgba(225, 29, 72, 0.18)',
      }
    : {
        accent: '#0f766e',
        background:
          'linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
        border: 'rgba(15, 118, 110, 0.18)',
      }

  const availabilityLabel = isCurrentPractitionerRoom
    ? 'You are checked in'
    : isRosterAssignedRoom
    ? rosterAssignmentGroup?.status === 'current'
      ? 'Rostered now'
      : 'Rostered room'
    : roomOccupied
    ? 'Busy'
    : 'Available'

  const secondaryLabel = roomOccupied
    ? isCurrentPractitionerRoom
      ? 'Your active session'
      : `Present: ${roomData?.checkedInMentorName || 'another mentor'}`
    : ''
  const auditPractitionerName = getAuditPractitionerName(roomData, isCurrentPractitionerRoom)
  const auditButtonLabel = `Video Audit ${auditPractitionerName}`

  const persistActiveRoomContext = (roomContext, serviceEntry, methodId) => {
    const effectiveRoomContext = roomContext || roomData
    const activeCheckInOptionId =
      getStoredCheckInOptionId(checkedInDetails) || serviceEntry?.checkInOption?.id || null

    const nextCheckInDetails = {
      ...(checkedInDetails || {}),
      clinicBrandId: clinicLocationData?.clinicBrandId,
      clinicLocationId: clinicLocationData?.id,
      chamberRoomId: roomData?.id,
      roomName:
        effectiveRoomContext?.title || effectiveRoomContext?.name || roomData?.title || 'Room unit',
      selectedCheckInEventOption: activeCheckInOptionId,
      checkinMethod: methodId,
      checkInMethod: methodId,
    }

    updateCheckInWithValidation(nextCheckInDetails)
    broadcastCheckInUpdate(nextCheckInDetails, effectiveRoomContext)

    localStorage.setItem('peekedInRoomDetails', JSON.stringify(effectiveRoomContext))

    const roomEventTypesToStore =
      serviceEntry?.roomEventTypes?.length > 0
        ? serviceEntry.roomEventTypes
        : dedupeEventTypes(
            roomServiceEntries.flatMap((serviceRow) => serviceRow?.roomEventTypes || []),
          )

    if (roomEventTypesToStore.length > 0) {
      localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypesToStore))
    } else {
      localStorage.removeItem('roomDataEventTypes')
    }

    return nextCheckInDetails
  }

  const navigateToCheckedInWorkspace = (checkInMethod = null) => {
    const resolvedMethodId = checkInMethod || activeRoomMethodId
    const nextRoute =
      resolvedMethodId === REMOTE_CHECKIN_METHOD
        ? `/mentor-home/join-chamber-room/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`
        : `/mentor-home/my-clinic-associations/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`

    navigate(nextRoute)
  }

  const handleOpenCheckedInWorkspace = async () => {
    const resumeStateKey = '__resume-current-room__'
    if (activeCheckInService) return

    const resumeMethodId = activeRoomMethodId
    const resumeServiceEntry = preferredRoomServiceEntry
    const resumeOptionId = getStoredCheckInOptionId(checkedInDetails) || resumeServiceEntry?.checkInOption?.id

    setActiveCheckInService(resumeStateKey)

    try {
      if (resumeOptionId) {
        const response = await refreshCheckIntoRoom({
          selectedCheckinEventOption: resumeOptionId,
          checkinMethod: resumeMethodId,
          clinicBrandId: clinicLocationData?.clinicBrandId,
          clinicLocationId: clinicLocationData?.id,
          chamberRoomId: roomData?.id,
        })

        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const emrUsageLog = response?.data?.data?.['emr-usage-log']
          const chamberRoom = response?.data?.data?.['chamber-room']

          if (emrUsageLog) {
            updateCheckInWithValidation({
              ...emrUsageLog,
              roomName: chamberRoom?.title || roomData?.title,
            })
            broadcastCheckInUpdate(emrUsageLog, chamberRoom || roomData)
          } else {
            persistActiveRoomContext(chamberRoom || roomData, resumeServiceEntry, resumeMethodId)
          }

          if (chamberRoom) {
            localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
          }

          const roomEventTypesToStore =
            resumeServiceEntry?.roomEventTypes?.length > 0
              ? resumeServiceEntry.roomEventTypes
              : dedupeEventTypes(
                  roomServiceEntries.flatMap((serviceRow) => serviceRow?.roomEventTypes || []),
                )

          if (roomEventTypesToStore.length > 0) {
            localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypesToStore))
          } else {
            localStorage.removeItem('roomDataEventTypes')
          }

          navigateToCheckedInWorkspace(resumeMethodId)
          return
        }
      }

      persistActiveRoomContext(roomData, resumeServiceEntry, resumeMethodId)
      navigateToCheckedInWorkspace(resumeMethodId)
    } catch (error) {
      console.error('Error reopening checked-in workspace:', error)
      persistActiveRoomContext(roomData, resumeServiceEntry, resumeMethodId)
      navigateToCheckedInWorkspace(resumeMethodId)
    } finally {
      setActiveCheckInService(null)
    }
  }

  const handleRefresh = async () => {
    if (!onRefreshChamberStatuses) return
    setRefreshing(true)
    try {
      await onRefreshChamberStatuses()
    } finally {
      setRefreshing(false)
    }
  }

  const handlePeekIn = async () => {
    const roomContext = {
      ...roomData,
      clinicBrandId: clinicLocationData?.clinicBrandId || roomData?.clinicBrandId,
      clinicLocationId: clinicLocationData?.id || roomData?.clinicLocationId,
    }
    const roomEventTypesToStore = dedupeEventTypes(
      roomServiceEntries.flatMap((serviceEntry) => serviceEntry.roomEventTypes || []),
    )

    localStorage.setItem('peekedInRoomDetails', JSON.stringify(roomContext))
    if (roomEventTypesToStore.length > 0) {
      localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypesToStore))
    } else {
      localStorage.removeItem('roomDataEventTypes')
    }

    navigate(
      `/mentor-home/my-clinic-associations/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`,
    )

    try {
      await peekIntoRoom({
        ...roomContext,
        logEventId: 'enter-without-checkin',
      })
    } catch (error) {
      console.error('Error logging peek-in:', error)
    }
  }

  const handleDirectCheckIn = async (serviceEntry) => {
    if (!serviceEntry?.checkInOption?.id || activeCheckInService) return

    setActiveCheckInService(serviceEntry.key)
    try {
      const response = await checkIntoRoom({
        ...roomData,
        clinicBrandId: clinicLocationData?.clinicBrandId,
        clinicLocationId: clinicLocationData?.id,
        selectedCheckinEventOption: serviceEntry.checkInOption.id,
        checkinMethod: selectedCheckInMethod,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const emrUsageLog = response?.data?.data?.['emr-usage-log']
        const chamberRoom = response?.data?.data?.['chamber-room']
        const updatedCheckInDetails = emrUsageLog
          ? {
              ...emrUsageLog,
              roomName: chamberRoom?.title,
            }
          : null

        if (updatedCheckInDetails) {
          updateCheckInWithValidation(updatedCheckInDetails)
          broadcastCheckInUpdate(updatedCheckInDetails, chamberRoom)
        }

        if (chamberRoom) {
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
        }

        if (serviceEntry.roomEventTypes?.length > 0) {
          localStorage.setItem('roomDataEventTypes', JSON.stringify(serviceEntry.roomEventTypes))
        } else {
          localStorage.removeItem('roomDataEventTypes')
        }

        navigateToCheckedInWorkspace(selectedCheckInMethod)
      }
    } finally {
      setActiveCheckInService(null)
    }
  }

  const handleSubmitCheckoutRequest = async (reason) => {
    setRequestCheckoutLoading(true)

    try {
      const { request, response } = await createEmrCheckInRequest({
        chamberRoomId: roomData?.id,
        reason,
      })

      if (request) {
        setRequestCheckoutDialogOpen(false)
        toast.success(
          `Checkout request sent to ${roomData?.checkedInMentorName || 'the checked-in mentor'}.`,
        )
        return
      }

      toast.error(
        getCheckInRequestErrorMessage(response, 'Could not send the checkout request.'),
      )
    } catch (error) {
      console.error('Error sending checkout request:', error)
      toast.error('Could not send the checkout request.')
    } finally {
      setRequestCheckoutLoading(false)
    }
  }

  // One-tap check-in: each entry is a service the practitioner can enter directly with the
  // selected method. The panel below makes these the prominent primary action.
  const checkInServiceEntries = !roomOccupied
    ? roomServiceEntries.filter((serviceEntry) => Boolean(serviceEntry?.checkInOption?.id))
    : []
  const singleCheckInService = checkInServiceEntries.length === 1

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: `1px solid ${tone.border}`,
        borderLeft: `4px solid ${tone.accent}`,
        background: tone.background,
        boxShadow: isCheckedInRoom ? '0 4px 14px rgba(124, 58, 237, 0.12)' : 'none',
        p: { xs: 1.5, sm: 1.75 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        height: '100%',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.25,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '16px', sm: '17px' },
                  fontWeight: 700,
                  color: 'rgba(15, 23, 42, 0.94)',
                }}
              >
                <HighlightedText text={roomData?.title || 'Room unit'} query={searchQuery} />
              </Typography>
              <Chip
                size="small"
                label={availabilityLabel}
                sx={{
                  backgroundColor: alpha(tone.accent, 0.12),
                  color: tone.accent,
                  fontWeight: 700,
                  border: `1px solid ${alpha(tone.accent, 0.18)}`,
                }}
              />
              {isRosterAssignedRoom && (
                <Chip
                  size="small"
                  label={
                    rosterAssignmentGroup?.status === 'current' ? 'Rostered now' : 'Rostered today'
                  }
                  sx={{
                    backgroundColor: 'rgba(2, 132, 199, 0.12)',
                    color: 'rgba(3, 105, 161, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(2, 132, 199, 0.16)',
                  }}
                />
              )}
              {isCheckedInRoom && (
                <Chip
                  size="small"
                  label="Active room"
                  sx={{
                    backgroundColor: 'rgba(124, 58, 237, 0.92)',
                    color: 'white',
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>
            {secondaryLabel && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: 'rgba(71, 85, 105, 0.88)',
                  fontSize: { xs: '12.5px', sm: '13px' },
                }}
              >
                {secondaryLabel}
              </Typography>
            )}
          </Box>

          {hasScreen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ChamberRoomStatusIndicator
                status={roomScreenStatus}
                chamberRoomName={roomData?.title}
                compact={true}
              />
              <Tooltip title="Refresh room status">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                      color: 'rgba(124, 58, 237, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(124, 58, 237, 0.08)',
                      },
                    }}
                  >
                    <Refresh
                      sx={{
                        fontSize: 18,
                        animation: refreshing ? 'room-status-spin 0.8s linear infinite' : 'none',
                        '@keyframes room-status-spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          minWidth: 0,
          width: '100%',
        }}
      >
        {/* PRIMARY — available room: a stand-out, one-tap check-in panel */}
        {checkInServiceEntries.length > 0 && (
          <Box
            sx={{
              borderRadius: '12px',
              border: `1px solid ${alpha(tone.accent, 0.3)}`,
              backgroundColor: alpha(tone.accent, 0.07),
              p: { xs: 1, sm: 1.25 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: tone.accent,
                }}
              >
                Check in
              </Typography>
              {checkInMethodOptions.length > 1 && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    gap: '2px',
                    p: '3px',
                    borderRadius: '999px',
                    backgroundColor: alpha(tone.accent, 0.1),
                  }}
                >
                  {checkInMethodOptions.map((methodOption) => {
                    const isSelectedMethod = selectedCheckInMethod === methodOption?.id
                    const methodEnabled = isCheckInMethodEnabled(methodOption)
                    return (
                      <ButtonBase
                        key={methodOption?.id}
                        disabled={!methodEnabled || Boolean(activeCheckInService)}
                        onClick={() => setSelectedCheckInMethod(methodOption?.id)}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                          color: isSelectedMethod ? '#fff' : tone.accent,
                          backgroundColor: isSelectedMethod ? tone.accent : 'transparent',
                          opacity: methodEnabled ? 1 : 0.4,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {methodOption?.title}
                      </ButtonBase>
                    )
                  })}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {checkInServiceEntries.map((serviceEntry) => {
                const loadingThisService = activeCheckInService === serviceEntry.key
                return (
                  <Button
                    key={serviceEntry.key}
                    variant="contained"
                    disableElevation
                    disabled={Boolean(activeCheckInService)}
                    onClick={() => handleDirectCheckIn(serviceEntry)}
                    startIcon={
                      loadingThisService ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Login sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{
                      flexGrow: 1,
                      textTransform: 'none',
                      borderRadius: '10px',
                      minHeight: 46,
                      px: 2,
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#fff',
                      backgroundColor: tone.accent,
                      boxShadow: `0 4px 12px ${alpha(tone.accent, 0.3)}`,
                      '&:hover': {
                        backgroundColor: tone.accent,
                        filter: 'brightness(0.94)',
                        boxShadow: `0 6px 16px ${alpha(tone.accent, 0.4)}`,
                      },
                    }}
                  >
                    {loadingThisService
                      ? 'Checking in…'
                      : singleCheckInService
                      ? 'Check in'
                      : `Check in · ${serviceEntry.label}`}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )}

        {/* PRIMARY — your active room: resume in one tap */}
        {isCurrentPractitionerRoom && (
          <Button
            variant="contained"
            disableElevation
            disabled={Boolean(activeCheckInService)}
            onClick={handleOpenCheckedInWorkspace}
            startIcon={
              activeCheckInService === '__resume-current-room__' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Login sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              minHeight: 46,
              px: 2,
              fontWeight: 700,
              fontSize: '14px',
              color: '#fff',
              backgroundColor: tone.accent,
              boxShadow: `0 4px 12px ${alpha(tone.accent, 0.3)}`,
              '&:hover': { backgroundColor: tone.accent, filter: 'brightness(0.94)' },
            }}
          >
            {activeCheckInService === '__resume-current-room__'
              ? activeRoomMethodId === REMOTE_CHECKIN_METHOD
                ? 'Joining chamber…'
                : 'Opening chamber…'
              : activeRoomMethodId === REMOTE_CHECKIN_METHOD
              ? 'Join chamber'
              : 'Open chamber'}
          </Button>
        )}

        {/* PRIMARY — occupied by another: peek-in is the way in */}
        {roomOccupied && !isCurrentPractitionerRoom && (
          <Button
            variant="contained"
            disableElevation
            disabled={Boolean(activeCheckInService) || requestCheckoutLoading}
            onClick={handlePeekIn}
            startIcon={<Visibility sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              minHeight: 46,
              px: 2,
              fontWeight: 700,
              fontSize: '14px',
              color: '#fff',
              backgroundColor: tone.accent,
              boxShadow: `0 4px 12px ${alpha(tone.accent, 0.3)}`,
              '&:hover': { backgroundColor: tone.accent, filter: 'brightness(0.94)' },
            }}
          >
            Peek In / Attend Video Calls
          </Button>
        )}

        {/* SECONDARY actions */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {!roomOccupied && (
            <Button
              variant="outlined"
              color="emrSecondary"
              disabled={Boolean(activeCheckInService)}
              onClick={handlePeekIn}
              startIcon={<Visibility sx={{ fontSize: 18 }} />}
              sx={{
                flexGrow: 1,
                textTransform: 'none',
                borderRadius: '10px',
                minHeight: 40,
                px: 2,
              }}
            >
              Peek In / Attend Video Calls
            </Button>
          )}

          {roomOccupied && !isCurrentPractitionerRoom && (
            <Button
              variant="outlined"
              color="warning"
              disabled={Boolean(activeCheckInService) || requestCheckoutLoading}
              onClick={() => setRequestCheckoutDialogOpen(true)}
              startIcon={
                requestCheckoutLoading ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                flexGrow: 1,
                textTransform: 'none',
                borderRadius: '10px',
                minHeight: 40,
                px: 2,
              }}
            >
              {requestCheckoutLoading ? 'Sending request…' : 'Request checkout'}
            </Button>
          )}

          {isCurrentPractitionerRoom && (
            <Box sx={{ flexGrow: 1 }}>
              <CheckoutButtonComponent
                styleProp={{
                  borderRadius: '10px',
                  minHeight: '40px',
                  padding: '0 20px',
                  width: '100%',
                }}
                buttonColor="error"
              />
            </Box>
          )}

          {canAuditAnotherPractitioner && (
            <Button
              variant="outlined"
              color="emrSecondary"
              aria-label={auditButtonLabel}
              disabled={Boolean(activeCheckInService)}
              onClick={() => {
                localStorage.setItem('auditRoomDetails', JSON.stringify(roomData))
                navigate(
                  `/mentor-home/join-as-auditor/${roomData?.id}/${roomData?.activeAuditChannelId}`,
                )
              }}
              startIcon={<Videocam sx={{ fontSize: 18 }} />}
              sx={{
                flexGrow: 1,
                textTransform: 'none',
                borderRadius: '10px',
                minHeight: 40,
                px: 2,
                minWidth: 0,
                justifyContent: 'flex-start',
              }}
            >
              <Box
                component="span"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                Video audit{auditPractitionerName ? ` · ${auditPractitionerName}` : ''}
              </Box>
            </Button>
          )}
        </Box>
      </Box>

      <CheckinRequestPromptDialog
        open={requestCheckoutDialogOpen}
        roomData={roomData}
        clinicLocationData={clinicLocationData}
        onClose={() => {
          if (!requestCheckoutLoading) {
            setRequestCheckoutDialogOpen(false)
          }
        }}
        onSubmit={handleSubmitCheckoutRequest}
        loading={requestCheckoutLoading}
      />
    </Box>
  )
}

function LocationOperationsSection({
  brand,
  location,
  waitingPatientsData,
  checkedInDetails,
  rosterAssignmentGroup = null,
  rosterAssignmentByRoomId = {},
  onNavigateToHotline,
  screenStatusesList,
  onRefreshChamberStatuses,
  searchQuery = '',
}) {
  const { mentorProfileData } = useContext(MentorProfileContext)

  const locationKey = `${brand?.id}-${location?.id}`
  const locationMetrics = waitingPatientsData?.[locationKey] || {}
  const locationStatus = useMemo(() => getClinicLocationStatus(location), [location])
  const isCheckedInLocation =
    checkedInDetails?.clinicLocationId === location?.id &&
    checkedInDetails?.clinicBrandId === brand?.id
  const waitingCount = locationMetrics?.count || 0
  const isRosterAssignedLocation = Boolean(rosterAssignmentGroup?.assignmentCount)

  const rooms = useMemo(
    () =>
      [...(location?.rooms || [])].sort((roomA, roomB) => {
        const roomRank = (roomItem) => {
          const roomRosterAssignment = rosterAssignmentByRoomId?.[roomItem?.id]
          const isActiveCheckedInRoom =
            checkedInDetails?.chamberRoomId === roomItem?.id &&
            checkedInDetails?.clinicLocationId === location?.id
          const occupiedByMe =
            roomItem?.occupied && roomItem?.checkedInMentorId === mentorProfileData?.id
          if (isActiveCheckedInRoom || occupiedByMe) return 0
          if (roomRosterAssignment?.status === 'current') return 1
          if (roomRosterAssignment?.status === 'upcoming') return 2
          if (!roomItem?.occupied) return 3
          if (roomItem?.canVideoAudit && roomItem?.activeAuditChannelId) return 4
          return 5
        }

        const rankDifference = roomRank(roomA) - roomRank(roomB)
        if (rankDifference !== 0) return rankDifference
        return (roomA?.title || '').localeCompare(roomB?.title || '')
      }),
    [
      checkedInDetails?.chamberRoomId,
      checkedInDetails?.clinicLocationId,
      location?.id,
      location?.rooms,
      mentorProfileData?.id,
      rosterAssignmentByRoomId,
    ],
  )

  const hasSecondaryActions = Boolean(location?.hasHotline)

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: isCheckedInLocation
          ? '1px solid rgba(124, 58, 237, 0.26)'
          : isRosterAssignedLocation
          ? '1px solid rgba(2, 132, 199, 0.22)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        background: isCheckedInLocation
          ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
          : isRosterAssignedLocation
          ? 'linear-gradient(135deg, rgba(2, 132, 199, 0.06) 0%, rgba(255, 255, 255, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {/* Title + current/rostered badges */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '16px', sm: '18px' },
                    fontWeight: 700,
                    color: 'rgba(15, 23, 42, 0.95)',
                  }}
                >
                  <HighlightedText text={location?.name} query={searchQuery} />
                </Typography>
                {isCheckedInLocation && (
                  <Chip
                    size="small"
                    label="Current clinic"
                    sx={{
                      backgroundColor: 'rgba(124, 58, 237, 0.92)',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}
                {isRosterAssignedLocation && (
                  <Chip
                    size="small"
                    label={
                      rosterAssignmentGroup?.status === 'current'
                        ? 'Rostered here now'
                        : 'Rostered here today'
                    }
                    sx={{
                      backgroundColor: 'rgba(2, 132, 199, 0.12)',
                      color: 'rgba(3, 105, 161, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(2, 132, 199, 0.16)',
                    }}
                  />
                )}
              </Box>
              {/* Status row: open/closed + waiting + next */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.75,
                }}
              >
                <Chip
                  size="small"
                  label={locationStatus.label}
                  sx={{
                    backgroundColor: alpha(locationStatus.accent, 0.12),
                    color: locationStatus.accent,
                    fontWeight: 700,
                    border: `1px solid ${alpha(locationStatus.accent, 0.18)}`,
                  }}
                />
                {Boolean(waitingCount) && (
                  <Chip
                    size="small"
                    icon={<People sx={{ fontSize: '16px !important' }} />}
                    label={`${waitingCount} waiting`}
                    sx={{
                      backgroundColor: 'rgba(124, 58, 237, 0.1)',
                      color: 'rgba(124, 58, 237, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(124, 58, 237, 0.14)',
                    }}
                  />
                )}
                {Boolean(locationMetrics?.earliestTime) && (
                  <Chip
                    size="small"
                    icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                    label={`Next ${dayjs.utc(locationMetrics.earliestTime).local().format('h:mm A')}`}
                    sx={{
                      backgroundColor: 'rgba(249, 115, 22, 0.1)',
                      color: 'rgba(194, 65, 12, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(249, 115, 22, 0.14)',
                    }}
                  />
                )}
              </Box>
            </Box>

          </Box>
        </Box>

      <Box sx={{ px: { xs: 1.75, sm: 2.25 }, pb: { xs: 1.75, sm: 2.25 } }}>
          {hasSecondaryActions && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {Boolean(location?.hasHotline) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onNavigateToHotline?.(location)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    minHeight: 36,
                  }}
                >
                  Join Hotline
                </Button>
              )}
            </Box>
          )}

          <Divider
            sx={{
              my: hasSecondaryActions ? 2 : 0,
              mb: 2,
              borderColor: 'rgba(148, 163, 184, 0.16)',
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(300px, 1fr))' },
              alignItems: 'start',
              gap: 1.25,
            }}
          >
            {rooms.length > 0 ? (
              rooms.map((roomItem) => (
                <RoomOperationsRow
                  key={roomItem?.id}
                  roomData={roomItem}
                  clinicLocationData={location}
                  checkedInDetails={checkedInDetails}
                  onRefreshChamberStatuses={onRefreshChamberStatuses}
                  screenStatusesList={screenStatusesList}
                  rosterAssignmentGroup={rosterAssignmentByRoomId?.[roomItem?.id] || null}
                  searchQuery={searchQuery}
                />
              ))
            ) : (
              <Box
                sx={{
                  borderRadius: '18px',
                  border: '1px dashed rgba(148, 163, 184, 0.34)',
                  backgroundColor: 'rgba(248, 250, 252, 0.9)',
                  px: 2,
                  py: 2.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(71, 85, 105, 0.88)',
                  }}
                >
                  No room units are linked to this location yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
    </Box>
  )
}

export default function MentorClinicAssociationsListView({
  brands = [],
  waitingPatientsData = {},
  checkedInDetails = null,
  onNavigateToHotline,
  screenStatusesList = [],
  onRefreshChamberStatuses,
  rosterAssignmentIndex = EMPTY_ROSTER_ASSIGNMENT_INDEX,
  searchQuery = '',
}) {
  const [expandedBrandIds, setExpandedBrandIds] = useState([])
  const rosterAssignmentsByBrandId = rosterAssignmentIndex.byBrandId
  const rosterAssignmentsByLocationKey = rosterAssignmentIndex.byLocationKey
  const rosterAssignmentsByRoomId = rosterAssignmentIndex.byRoomId
  const orderedBrands = useMemo(() => {
    const brandStatusPriority = {
      current: 1,
      upcoming: 2,
      completed: 3,
    }

    return brands
      .map((brand, index) => ({
        brand,
        index,
        isCheckedInBrand: checkedInDetails?.clinicBrandId === brand?.id,
        rosterAssignmentGroup: rosterAssignmentsByBrandId?.[brand?.id] || null,
      }))
      .sort((leftBrand, rightBrand) => {
        if (leftBrand.isCheckedInBrand !== rightBrand.isCheckedInBrand) {
          return leftBrand.isCheckedInBrand ? -1 : 1
        }

        const leftRosterPriority = leftBrand.rosterAssignmentGroup
          ? brandStatusPriority?.[leftBrand.rosterAssignmentGroup.status] ?? 99
          : 99
        const rightRosterPriority = rightBrand.rosterAssignmentGroup
          ? brandStatusPriority?.[rightBrand.rosterAssignmentGroup.status] ?? 99
          : 99

        if (leftRosterPriority !== rightRosterPriority) {
          return leftRosterPriority - rightRosterPriority
        }

        return leftBrand.index - rightBrand.index
      })
      .map(({ brand }) => brand)
  }, [brands, checkedInDetails?.clinicBrandId, rosterAssignmentsByBrandId])

  // Auto-expand only the top brand and any brand that currently has patients waiting; the rest
  // start collapsed. Once the user toggles a brand we stop auto-managing it, so their choice
  // sticks even as live data refreshes.
  const userToggledBrandIdsRef = useRef(new Set())

  const isSearching = Boolean(searchQuery.trim())
  useEffect(() => {
    setExpandedBrandIds((previousExpandedBrandIds) => {
      const previousSet = new Set(previousExpandedBrandIds)
      const nextExpanded = new Set()
      orderedBrands.forEach((brand, index) => {
        const brandId = brand?.id
        if (!brandId) return
        // While searching, open every (filtered) brand so the matches are visible at once.
        if (isSearching) {
          nextExpanded.add(brandId)
          return
        }
        if (userToggledBrandIdsRef.current.has(brandId)) {
          if (previousSet.has(brandId)) nextExpanded.add(brandId)
          return
        }
        const hasWaiting = (brand?.locations || []).some(
          (location) => (waitingPatientsData?.[`${brandId}-${location?.id}`]?.count || 0) > 0,
        )
        if (index === 0 || hasWaiting) nextExpanded.add(brandId)
      })
      return [...nextExpanded]
    })
  }, [orderedBrands, waitingPatientsData, isSearching])

  const toggleBrandExpansion = (brandId) => {
    if (!brandId) return

    userToggledBrandIdsRef.current.add(brandId)
    setExpandedBrandIds((previousExpandedBrandIds) =>
      previousExpandedBrandIds.includes(brandId)
        ? previousExpandedBrandIds.filter((expandedBrandId) => expandedBrandId !== brandId)
        : [...previousExpandedBrandIds, brandId],
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(300px, 1fr))' },
        alignItems: 'start',
        gap: { xs: 2, sm: 2.5 },
      }}
    >
      {orderedBrands.map((brand) => {
        const brandLocationKeys = (brand?.locations || []).map(
          (location) => `${brand?.id}-${location?.id}`,
        )
        const brandWaiting = brandLocationKeys.reduce(
          (total, key) => total + (waitingPatientsData?.[key]?.count || 0),
          0,
        )
        const brandBooked = brandLocationKeys.reduce(
          (total, key) => total + (waitingPatientsData?.[key]?.totalBookings || 0),
          0,
        )
        const isActiveBrand = checkedInDetails?.clinicBrandId === brand?.id
        const brandRosterAssignmentGroup = rosterAssignmentsByBrandId?.[brand?.id] || null
        const isRosterAssignedBrand = Boolean(brandRosterAssignmentGroup?.assignmentCount)
        const isBrandExpanded = expandedBrandIds.includes(brand?.id)
        const orderedLocations = [...(brand?.locations || [])]
          .map((location, index) => ({
            location,
            index,
            isCheckedInLocation:
              checkedInDetails?.clinicLocationId === location?.id &&
              checkedInDetails?.clinicBrandId === brand?.id,
            rosterAssignmentGroup:
              rosterAssignmentsByLocationKey?.[`${brand?.id}-${location?.id}`] || null,
          }))
          .sort((leftLocation, rightLocation) => {
            const locationStatusPriority = {
              current: 1,
              upcoming: 2,
              completed: 3,
            }

            if (leftLocation.isCheckedInLocation !== rightLocation.isCheckedInLocation) {
              return leftLocation.isCheckedInLocation ? -1 : 1
            }

            const leftRosterPriority = leftLocation.rosterAssignmentGroup
              ? locationStatusPriority?.[leftLocation.rosterAssignmentGroup.status] ?? 99
              : 99
            const rightRosterPriority = rightLocation.rosterAssignmentGroup
              ? locationStatusPriority?.[rightLocation.rosterAssignmentGroup.status] ?? 99
              : 99

            if (leftRosterPriority !== rightRosterPriority) {
              return leftRosterPriority - rightRosterPriority
            }

            return leftLocation.index - rightLocation.index
          })
          .map(({ location }) => location)

        return (
          <Box
            key={brand?.id}
            sx={{
              gridColumn: isBrandExpanded ? '1 / -1' : 'auto',
              borderRadius: '16px',
              border: isActiveBrand
                ? '1.5px solid rgba(124, 58, 237, 0.45)'
                : isRosterAssignedBrand
                ? '1.5px solid rgba(2, 132, 199, 0.4)'
                : '1px solid rgba(100, 116, 139, 0.3)',
              background: isActiveBrand
                ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(255, 255, 255, 1) 100%)'
                : isRosterAssignedBrand
                ? 'linear-gradient(135deg, rgba(2, 132, 199, 0.08) 0%, rgba(255, 255, 255, 1) 100%)'
                : '#ffffff',
              boxShadow: isActiveBrand
                ? '0 10px 24px rgba(124, 58, 237, 0.18)'
                : isRosterAssignedBrand
                ? '0 8px 20px rgba(2, 132, 199, 0.14)'
                : '0 6px 18px rgba(15, 23, 42, 0.10), 0 1px 3px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <ButtonBase
              onClick={() => toggleBrandExpansion(brand?.id)}
              sx={{
                width: '100%',
                textAlign: 'left',
                px: { xs: 1.75, sm: 2.25 },
                py: { xs: 1.75, sm: 2 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1.5,
                flexWrap: 'wrap',
                '&:hover': {
                  backgroundColor: alpha(isActiveBrand ? '#7c3aed' : '#0f172a', 0.02),
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <BrandIcon brand={brand} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: { xs: '18px', sm: '20px', md: '22px' },
                        fontWeight: 700,
                        color: 'rgba(15, 23, 42, 0.96)',
                      }}
                    >
                      <HighlightedText text={brand?.title} query={searchQuery} />
                    </Typography>
                    {isActiveBrand && (
                      <Chip
                        size="small"
                        label="Checked in here"
                        sx={{
                          backgroundColor: 'rgba(124, 58, 237, 0.92)',
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {isRosterAssignedBrand && (
                      <Chip
                        size="small"
                        label={
                          brandRosterAssignmentGroup?.status === 'current'
                            ? 'Rostered here now'
                            : 'Rostered here today'
                        }
                        sx={{
                          backgroundColor: 'rgba(2, 132, 199, 0.12)',
                          color: 'rgba(3, 105, 161, 0.94)',
                          fontWeight: 700,
                          border: '1px solid rgba(2, 132, 199, 0.16)',
                        }}
                      />
                    )}
                  </Box>
                  {brandBooked === 0 && (
                    <Typography
                      sx={{
                        mt: 0.5,
                        color: 'rgba(100, 116, 139, 0.85)',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      No patients booked today
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  ml: 'auto',
                }}
              >
                {isRosterAssignedBrand && Boolean(brandRosterAssignmentGroup?.detailLabel) && (
                  <Chip
                    size="small"
                    icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                    label={brandRosterAssignmentGroup.detailLabel}
                    sx={{
                      backgroundColor: 'rgba(236, 254, 255, 0.88)',
                      color: 'rgba(14, 116, 144, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(125, 211, 252, 0.35)',
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '999px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActiveBrand ? 'rgba(124, 58, 237, 0.96)' : 'rgba(51, 65, 85, 0.82)',
                    backgroundColor: alpha(isActiveBrand ? '#7c3aed' : '#e2e8f0', 0.1),
                    border: `1px solid ${alpha(isActiveBrand ? '#7c3aed' : '#64748b', 0.12)}`,
                    flexShrink: 0,
                  }}
                >
                  {isBrandExpanded ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </Box>
            </ButtonBase>

            {brandBooked > 0 && (
              <Box
                role="button"
                tabIndex={-1}
                onClick={() => toggleBrandExpansion(brand?.id)}
                sx={{ cursor: 'pointer' }}
              >
                <WaitingPatientsStrip
                  waitingCount={brandWaiting}
                  bookingCount={brandBooked}
                  showNextTime={false}
                />
              </Box>
            )}

            <Collapse in={isBrandExpanded} timeout="auto">
              <Box
                sx={{
                  px: { xs: 2, sm: 2.5, md: 3 },
                  pb: { xs: 2, sm: 2.5, md: 3 },
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'repeat(auto-fill, minmax(440px, 1fr))' },
                  alignItems: 'start',
                  gap: 2,
                }}
              >
                {orderedLocations.map((location) => (
                  <LocationOperationsSection
                    key={location?.id}
                    brand={brand}
                    location={location}
                    waitingPatientsData={waitingPatientsData}
                    checkedInDetails={checkedInDetails}
                    rosterAssignmentGroup={
                      rosterAssignmentsByLocationKey?.[`${brand?.id}-${location?.id}`] || null
                    }
                    rosterAssignmentByRoomId={rosterAssignmentsByRoomId}
                    onNavigateToHotline={onNavigateToHotline}
                    screenStatusesList={screenStatusesList}
                    onRefreshChamberStatuses={onRefreshChamberStatuses}
                    searchQuery={searchQuery}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )
      })}
    </Box>
  )
}
